import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { VirtuosoGrid, type GridComponents } from 'react-virtuoso';
import i18n from '../../i18n';

/** 底部 loading、滚动区类名、顶部区渲染 ref（每实例独立，避免多列表互相覆盖） */
type VirtuosoPagedGridLayoutContextValue = {
    /** 触底加载下一页时为 true */
    loadingMore: boolean;
    /** Footer 区域 className */
    footerClassName: string;
    /** Scroller 上除 Virtuoso 注入类名外的额外 className */
    scrollerClassName: string;
    /** 指向最新 `header` 渲染闭包；`Header` 组件引用稳定，通过 ref 读最新内容 */
    headerRenderRef: React.MutableRefObject<(() => React.ReactNode) | null>;
};

const VirtuosoPagedGridLayoutContext = React.createContext<VirtuosoPagedGridLayoutContextValue | null>(null);

/**
 * Virtuoso Grid 顶部区：组件引用稳定，内容由 `headerRenderRef` 指向的函数渲染。
 */
function VirtuosoPagedGridHeader() {
    const ctx = useContext(VirtuosoPagedGridLayoutContext);
    const fn = ctx?.headerRenderRef.current;
    return <>{fn != null ? fn() : null}</>;
}

/**
 * Virtuoso 滚动容器：合并业务侧 scroller 类名。
 */
const VirtuosoPagedGridScroller = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    function VirtuosoPagedGridScroller(scrollerProps, ref) {
        const { className, ...rest } = scrollerProps;
        const ctx = useContext(VirtuosoPagedGridLayoutContext);
        const scrollerClassName = ctx?.scrollerClassName ?? 'menu-store-page__grid-scroller';
        return (
            <div
                ref={ref}
                {...rest}
                className={[scrollerClassName, className].filter(Boolean).join(' ')}
            />
        );
    },
);

/**
 * 列表底部 loading。
 */
function VirtuosoPagedGridFooter() {
    const ctx = useContext(VirtuosoPagedGridLayoutContext);
    if (!ctx?.loadingMore) {
        return null;
    }
    return (
        <Box
            className={ctx.footerClassName}
            role="status"
            aria-label={i18n.t('general_text_loading')}
            aria-live="polite"
        >
            <CircularProgress size={28} />
        </Box>
    );
}

/** 静态 components，避免父级重渲染导致 Virtuoso 重置滚动 */
const VIRTUOSO_PAGED_GRID_COMPONENTS: GridComponents = {
    Scroller: VirtuosoPagedGridScroller,
    Header: VirtuosoPagedGridHeader,
    Footer: VirtuosoPagedGridFooter,
};

export type MenuStoreVirtuosoPagedGridProps<T> = {
    /** 当前已加载的数据行 */
    items: readonly T[];
    /** 触底加载中 */
    loadingMore: boolean;
    /** 触底或可视不足时拉取下一页 */
    onLoadMore: () => void;
    /** 变更时重置 Virtuoso 内部滚动位置 */
    listResetKey: string;
    /** 行 key */
    computeItemKey: (index: number, item: T) => React.Key;
    /** 单元格内容 */
    itemContent: (index: number, item: T) => React.ReactNode;
    /** Virtuoso List 的 className（如 flex/grid 布局） */
    listClassName: string;
    /** 每个 Item 包装 className */
    itemClassName: string;
    /** 外壳 className，默认与菜单商店列表一致 */
    shellClassName?: string;
    /** Virtuoso 根节点 className */
    gridRootClassName?: string;
    /** 列表顶部固定区（可选） */
    header?: React.ReactNode;
    /** Scroller 额外类名 */
    scrollerClassName?: string;
    /** Footer 区域类名 */
    footerClassName?: string;
    /** 纵向预渲染：底部像素 */
    increaseViewportBottom?: number;
    /** 纵向预渲染：顶部像素 */
    increaseViewportTop?: number;
    /** overscan.main / reverse */
    overscanMain?: number;
};

/**
 * 通用：虚拟栅格 + 触底分页 + 可选顶部区 + 底部 loading（菜单商店页、分享弹窗等复用）。
 */
export function MenuStoreVirtuosoPagedGrid<T>(props: MenuStoreVirtuosoPagedGridProps<T>) {
    const {
        items,
        loadingMore,
        onLoadMore,
        listResetKey,
        computeItemKey,
        itemContent,
        listClassName,
        itemClassName,
        shellClassName = 'menu-store-browse-grid__shell',
        gridRootClassName = 'menu-store-browse-grid',
        header = null,
        scrollerClassName = 'menu-store-page__grid-scroller',
        footerClassName = 'menu-store-browse-grid__footer',
        increaseViewportBottom = 24 * 8,
        increaseViewportTop = 24 * 6,
        overscanMain = 360,
    } = props;

    /** 本实例顶部区渲染：写入 ref 供稳定 `Header` 读取 */
    const headerRenderRef = useRef<(() => React.ReactNode) | null>(null);
    headerRenderRef.current = header != null ? () => header : null;

    const layoutValue = useMemo(
        (): VirtuosoPagedGridLayoutContextValue => ({
            loadingMore,
            footerClassName,
            scrollerClassName,
            headerRenderRef,
        }),
        [loadingMore, footerClassName, scrollerClassName],
    );

    const handleEndReached = useCallback(() => {
        onLoadMore();
    }, [onLoadMore]);

    const increaseViewportBy = useMemo(
        () => ({ bottom: increaseViewportBottom, top: increaseViewportTop }),
        [increaseViewportBottom, increaseViewportTop],
    );

    const overscan = useMemo(() => ({ main: overscanMain, reverse: overscanMain }), [overscanMain]);

    return (
        <Box className={shellClassName}>
            <VirtuosoPagedGridLayoutContext.Provider value={layoutValue}>
                <VirtuosoGrid<T>
                    key={listResetKey}
                    className={gridRootClassName}
                    style={{ height: '100%', width: '100%' }}
                    data={items}
                    computeItemKey={computeItemKey}
                    components={VIRTUOSO_PAGED_GRID_COMPONENTS}
                    endReached={handleEndReached}
                    increaseViewportBy={increaseViewportBy}
                    itemContent={itemContent}
                    listClassName={listClassName}
                    itemClassName={itemClassName}
                    overscan={overscan}
                />
            </VirtuosoPagedGridLayoutContext.Provider>
        </Box>
    );
}
